import { CRM_CONTACTS_CHANGED_EVENT } from "./contactImports";
import { fetchPrimary } from "../environments/primary/fetch";
import { isToolNotOfferedRefusal } from "./refusalCopy";
import type { CompanyRecordProjection } from "../components/crm/companyRecord";

export const CRM_COMPANIES_CHANGED_EVENT = "neucleos-crm-companies-changed";
export const CRM_FOLLOWUPS_CHANGED_EVENT = "neucleos-crm-followups-changed";

export interface CompanySummary {
  companyId: string;
  name: string;
  domain: string | null;
  revision: number;
  archived: boolean;
}
export interface CompanyRecord extends CompanyRecordProjection {
  company: CompanyRecordProjection["company"] & { revision: number; universeRef: string | null };
  reviewed: CompanyRecordProjection["written"];
}
export interface CrmPage<T> {
  page: T[];
  isDone: boolean;
  continueCursor: string;
}
export interface CrmActivity {
  _id: string;
  body: string;
  createdAt: number;
  author: string;
  type: string;
  visibility?: "author-private" | "shared";
  dueAt?: number;
  completedAt?: number;
  revision?: number;
  outcome?: string;
  meta?: { crmActivity?: { action: string } };
}

export async function crmPost<T>(worldKey: string, request: object): Promise<T> {
  const response = await fetchPrimary("/api/neucleos/crm", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ worldKey, ...request }),
  });
  const body = (await response.json().catch(() => null)) as {
    ok?: boolean;
    kind?: string;
    reason?: string;
    result?: T;
  } | null;
  if (!response.ok || !body?.ok || body.result === undefined) {
    if (isToolNotOfferedRefusal(body?.kind))
      throw new Error(
        "This World doesn't offer the CRM yet. It will appear when the World is updated; you don't need to sign in again.",
      );
    if (body?.kind === "not_signed_in")
      throw new Error("Sign in again in Settings → Account to use this World's CRM.");
    throw new Error(
      body?.reason?.replace(/^REFUSED:\s*/, "").split(". Law:")[0] ??
        "CRM could not be reached. Retry when connected.",
    );
  }
  if ("action" in request && request.action === "saveCompany" && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CRM_COMPANIES_CHANGED_EVENT, { detail: { worldKey } }));
  }
  if (
    "action" in request &&
    [
      "saveContact",
      "mergeContacts",
      "undoContactMerge",
      "linkCommunicationSource",
      "unlinkCommunicationSource",
    ].includes(String(request.action)) &&
    typeof window !== "undefined"
  )
    window.dispatchEvent(new CustomEvent(CRM_CONTACTS_CHANGED_EVENT, { detail: { worldKey } }));
  if (
    "action" in request &&
    ["recordActivity", "completeActivity", "rescheduleActivity", "setFollowUpReminder"].includes(
      String(request.action),
    ) &&
    typeof window !== "undefined"
  )
    window.dispatchEvent(new CustomEvent(CRM_FOLLOWUPS_CHANGED_EVENT, { detail: { worldKey } }));
  return body.result;
}
